import StatusBadge from "@/app/components/common/badge/StatusBadge";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { gxDateFormat } from "@/app/utils/helpers/gxDateFormat";
import { SvgIcon } from "@progress/kendo-react-common";
import { chevronDownIcon, chevronUpIcon } from "@progress/kendo-svg-icons";
import { memo, useState } from "react";
import { useMandateDetails } from "./apiHooks/useMandateDetails";
import type { MandateDetails } from "@/app/types/accounts";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { MaskedAccountField } from "@/app/utils/MaskedAccountField";
import ShimmerLoadingThree from "@/app/components/webloading/shimmer/ShimmerLoadingThree";

interface DirectDebitDetailsProps {
  directDebitId: number;
}

interface SingleMandateDetailsProp {
  data: MandateDetails | undefined;
}

const MandateInformation = ({ data }: SingleMandateDetailsProp) => {
  const [cardOpen, setCardOpen] = useState<boolean>(true);

  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (text: string | null | undefined) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // revert back after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  return (
    <div className="gx-card ">
      <button className="gx-card-header" onClick={() => setCardOpen(!cardOpen)}>
        <h2 className="gx-section-title gx-h3-b">{"Mandate Information"}</h2>
        <SvgIcon
          icon={cardOpen ? chevronUpIcon : chevronDownIcon}
          size="small"
        />
      </button>
      {cardOpen && (
        <div className="gx-card-body">
          <div className="gx-row">
            <span className="gx-t-lable">{"Mandate Reference"}</span>
            <span className="gx-t-value">
              {data?.referenceId ?? "NA"}{" "}
              <button
                className="ms-1 cursor-pointer"
                onClick={() => handleCopy(data?.referenceId)}
              >
                <img
                  className="h-3 w-3"
                  src={`${ICON_BASE_PATH}${copied ? "copied-black.svg" : "copy-icon.svg"}`}
                  alt="copy"
                />
              </button>
            </span>
          </div>
          <div className="gx-row">
            <span className="gx-t-lable">{"Direct Debit ID"}</span>
            <span className="gx-t-value">{data?.cbsDirectDebitId ?? "NA"}</span>
          </div>
          <div className="gx-row">
            <span className="gx-t-lable">{"Collection Frequency"}</span>
            <span className="gx-t-value">{data?.debitFrequency ?? "NA"}</span>
          </div>
          <div className="gx-row">
            <span className="gx-t-lable">{"Status"}</span>
            <span className="gx-t-value">
              <StatusBadge
                module={"directDebit"}
                statusCode={data?.debitStatus ?? undefined}
              />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const LastPayment = ({ data }: SingleMandateDetailsProp) => {
  const [cardOpen, setCardOpen] = useState<boolean>(true);
  return (
    <div className="gx-card ">
      <button className="gx-card-header" onClick={() => setCardOpen(!cardOpen)}>
        <h2 className="gx-section-title gx-h3-b">{"Last Payment"}</h2>
        <SvgIcon
          icon={cardOpen ? chevronUpIcon : chevronDownIcon}
          size="small"
        />
      </button>
      {cardOpen && (
        <div className="gx-card-body">
          <div className="gx-row">
            <span className="gx-t-lable">{"Last Payment Amount"}</span>
            {data?.lastPaymentAmount ? (
              <span className="gx-t-value">
                {
                  gxCurrencyFormat(
                    Number(data?.lastPaymentAmount),
                    data?.lastPaymentCurrency,
                  ).symbol
                }
                {
                  gxCurrencyFormat(
                    Number(data?.lastPaymentAmount),
                    data?.lastPaymentCurrency,
                  ).integer
                }
                {
                  gxCurrencyFormat(
                    Number(data?.lastPaymentAmount),
                    data?.lastPaymentCurrency,
                  ).decimal
                }
              </span>
            ) : (
              <span className="gx-t-value">{"NA"}</span>
            )}
          </div>
          <div className="gx-row">
            <span className="gx-t-lable">{"Last Payment Currency"}</span>
            <span className="gx-t-value">
              {data?.lastPaymentCurrency ?? "NA"}
            </span>
          </div>
          <div className="gx-row">
            <span className="gx-t-lable">{"Last Payment Date"}</span>
            <span className="gx-t-value">
              {data?.lastPaymentDate
                ? gxDateFormat(data?.lastPaymentDate)
                : "NA"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const AccountInformation = ({ data }: SingleMandateDetailsProp) => {
  const [cardOpen, setCardOpen] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (text: string | null | undefined) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="gx-card ">
      <button className="gx-card-header" onClick={() => setCardOpen(!cardOpen)}>
        <h2 className="gx-section-title gx-h3-b">{"Account Information"}</h2>
        <SvgIcon
          icon={cardOpen ? chevronUpIcon : chevronDownIcon}
          size="small"
        />
      </button>
      {cardOpen && (
        <div className="gx-card-body">
          <div className="gx-row-two">
            <div className="gx-inner-row">
              <span className="gx-t-lable">{"Account"}</span>
              <span className="gx-t-value">{data?.accountName ?? "NA"}</span>
            </div>
            <div className="gx-inner-row-two ">
              <span className="gx-t-value flex items-center">
                {<MaskedAccountField accountNumber={data?.accountNo ?? "NA"} />}
                <button
                  className="ms-2 cursor-pointer"
                  onClick={() => handleCopy(data?.accountNo)}
                >
                  <img
                    className="h-3 w-3"
                    src={`${ICON_BASE_PATH}${copied ? "copied-black.svg" : "copy-icon.svg"}`}
                    alt="copy"
                  />
                </button>
              </span>
            </div>
          </div>

          <div className="gx-row">
            <span className="gx-t-lable">{"Account Ownership"}</span>
            <span className="gx-t-value">{data?.partyType ?? "NA"}</span>
          </div>
          <div className="gx-row">
            <span className="gx-t-lable">{"Account Category"}</span>
            <span className="gx-t-value">{data?.partyCategory ?? "NA"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const DirectDebitDetails = memo(
  ({ directDebitId }: DirectDebitDetailsProps) => {
    const [isMandateDetailsLoading, setIsMandateDetailsLoading] =
      useState<boolean>(false);
    const { mandateDetails } = useMandateDetails({
      directDebitId,
      setIsMandateDetailsLoading,
    });
    const avatar = mandateDetails?.beneName?.slice(0, 3)?.toLocaleUpperCase();

    return isMandateDetailsLoading ? (
      <ShimmerLoadingThree />
    ) : (
      <div className="gx-direct-debit-details">
        <div className="gx-direct-debit-avatar-detail">
          <div className="gx-direct-debit-avatar-container">
            <div className="gx-direct-debit-avatar">
              <span className="gx-p24-b">{avatar}</span>
            </div>
          </div>
          <div className="gx-direct-debit-name">
            <span className="gx-p20-b">{mandateDetails?.beneName}</span>
          </div>
        </div>
        <MandateInformation data={mandateDetails} />
        <LastPayment data={mandateDetails} />
        <AccountInformation data={mandateDetails} />
        <div className="gx-direct-debit-detail-note">
          <img src={ICON_BASE_PATH + "info-icon-orange.svg"} alt="info" />
          <span className="gx-p10-r">
            {
              "To amend or cancel this direct debit, please contact your bank directly."
            }
          </span>
        </div>
      </div>
    );
  },
);

export default DirectDebitDetails;
